import { ChainType } from "@0xsquid/squid-types";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo } from "react";
import type { FullAddressData } from "../../core";
import { SwapParams } from "../../core/types/swap";
import { areSameAddress, formatHash } from "../../services";
import { useSquidChains } from "../chains/useSquidChains";
import {
  useConfigStore,
  useSwapRoutePersistStore,
  useTransactionStore,
} from "../store/useSquidStore";
import { useSquidTokens } from "../tokens/useSquidTokens";

import { getNewSwapParamsFromInput } from "../../services";
import {
  filterTokensForDestination,
  getFirstAvailableChainId,
  getInitialChainIdFromConfig,
  getInitialOrDefaultTokenAddressForChain,
  getTokensForChain,
  resolveChainIdFromAsset,
} from "../../services/internal/configService";
import { WidgetEvents } from "../../services/internal/eventService";
import { useEnsDataForAddress } from "../wallet/useEns";
import { useMultiChainWallet } from "../wallet/useMultiChainWallet";

export const useSwap = () => {
  const {
    initialAssets,
    defaultTokensPerChain,
    disabledChains,
    availableChains,
  } = useConfigStore((state) => state.config);
  const fromPrice = useTransactionStore((state) => state.fromPrice);
  const { swapRoute } = useSwapRoutePersistStore();
  const { tokens } = useSquidTokens();
  const queryClient = useQueryClient();

  const { chains, supportedDestinationChains, supportedSourceChains } =
    useSquidChains();

  const sourceChainId = useMemo(
    () =>
      swapRoute?.fromChainId ??
      (initialAssets?.from
        ? resolveChainIdFromAsset(initialAssets.from, supportedSourceChains)
        : undefined),
    [swapRoute?.fromChainId, supportedSourceChains, initialAssets?.from]
  );

  const destChainId = useMemo(
    () =>
      swapRoute?.toChainId ??
      (initialAssets?.to
        ? resolveChainIdFromAsset(initialAssets.to, supportedDestinationChains)
        : undefined),
    [swapRoute?.toChainId, supportedDestinationChains, initialAssets?.to]
  );

  const fromChain = useMemo(
    () =>
      supportedSourceChains.find((c) => c.chainId === swapRoute?.fromChainId),
    [swapRoute?.fromChainId, supportedSourceChains]
  );

  const toChain = useMemo(
    () =>
      supportedDestinationChains.find(
        (c) => c.chainId === swapRoute?.toChainId
      ),
    [swapRoute?.toChainId, supportedDestinationChains]
  );

  const fromTokens = useMemo(
    () => getTokensForChain(tokens, sourceChainId),
    [tokens, sourceChainId]
  );

  const fromToken = useMemo(
    () =>
      fromTokens.find(
        (t) =>
          t.address.toLowerCase() === swapRoute?.fromTokenAddress?.toLowerCase()
      ),
    [fromTokens, swapRoute?.fromTokenAddress]
  );

  const toTokens = useMemo(
    () =>
      filterTokensForDestination({
        tokens: getTokensForChain(tokens, destChainId),
        selectedDestinationChain: toChain,
        selectedSourceToken: fromToken,
      }),
    [destChainId, toChain, fromToken, tokens]
  );

  const toToken = useMemo(
    () =>
      toTokens.find(
        (t) =>
          t.address.toLowerCase() === swapRoute?.toTokenAddress?.toLowerCase()
      ),
    [toTokens, swapRoute?.toTokenAddress]
  );

  const isSameChain = sourceChainId === destChainId;

  const tokenItems = useMemo(
    () => ({ from: fromTokens, to: toTokens }),
    [fromTokens, toTokens]
  );

  const { connectedAddress: connectedDestinationAddress } =
    useMultiChainWallet(toChain);
  const { connectedAddress: connectedSourceAddress } =
    useMultiChainWallet(fromChain);

  const destAddressData = swapRoute?.destinationAddress?.address
    ? swapRoute?.destinationAddress
    : connectedDestinationAddress;

  const destAddressEnsData = useEnsDataForAddress({
    address: destAddressData.address,
    options: {
      // enabled only for EVM chains
      // and when there's no ENS data yet
      enabled:
        !destAddressData.ens?.name && toChain?.chainType === ChainType.EVM,
    },
  });

  const destinationAddress: FullAddressData = useMemo(() => {
    if (!toChain?.chainType) return {};

    return {
      ...destAddressData,
      ens: destAddressData.ens?.name
        ? destAddressData.ens
        : destAddressEnsData.data,
      formatted: formatHash({
        hash: destAddressData.address,
        chainType: toChain.chainType,
      }),
    };
  }, [destAddressData, destAddressEnsData.data, toChain?.chainType]);

  const fromPriceChanged = useCallback((price: string) => {
    useTransactionStore.setState({ fromPrice: price || undefined });
  }, []);

  /**
   * When user changes something from the SwapView
   */
  const onSwapChange = useCallback(
    (inputSwapParams: SwapParams) => {
      const newSwapParams = getNewSwapParamsFromInput({
        inputParams: inputSwapParams,
        initialSwapRoute: swapRoute,
        tokens,
        chains,
        config: {
          initialAssets,
          defaultTokensPerChain,
          disabledChains,
          availableChains,
        },
        queryClient,
        connectedSourceAddress: connectedSourceAddress.address,
      });

      const { fromChainId, toChainId, fromTokenAddress, toTokenAddress } =
        newSwapParams;

      const fromChainData = fromChainId
        ? chains.find((c) => c.chainId === fromChainId)
        : undefined;
      const toChainData = toChainId
        ? chains.find((c) => c.chainId === toChainId)
        : undefined;

      const fromTokenData =
        fromChainId && fromTokenAddress
          ? tokens.find(
              (t) =>
                t.chainId === fromChainId &&
                t.address.toLowerCase() === fromTokenAddress.toLowerCase()
            )
          : undefined;
      const toTokenData =
        toChainId && toTokenAddress
          ? tokens.find(
              (t) =>
                t.chainId === toChainId &&
                t.address.toLowerCase() === toTokenAddress.toLowerCase()
            )
          : undefined;

      WidgetEvents.getInstance().dispatchSwapParamsChanged({
        fromChainId,
        toChainId,
        fromTokenAddress,
        toTokenAddress,
        fromNetworkName: fromChainData?.networkName,
        toNetworkName: toChainData?.networkName,
        fromTokenSymbol: fromTokenData?.symbol,
        toTokenSymbol: toTokenData?.symbol,
      });

      useSwapRoutePersistStore.setState({
        swapRoute: newSwapParams,
      });

      return newSwapParams;
    },
    [
      chains,
      connectedSourceAddress.address,
      defaultTokensPerChain,
      disabledChains,
      availableChains,
      initialAssets,
      queryClient,
      swapRoute,
      tokens,
    ]
  );

  /**
   * Function to invert the selected tokens and chains
   *
   * If inverting is not possible, returns undefined, so the UI can react accordingly
   */
  const invertSwaps = useMemo(() => {
    if (!destChainId || !sourceChainId) return;

    // Check if inversion is allowed based on chain configurations
    const isToChainAvailableOnSource = supportedSourceChains.some(
      (c) => c.chainId === destChainId
    );

    if (!isToChainAvailableOnSource) return;

    const isFromChainAvailableOnDestination = supportedDestinationChains.some(
      (c) => c.chainId === sourceChainId
    );

    if (!isFromChainAvailableOnDestination) return;

    return () =>
      onSwapChange({
        fromChainId: destChainId,
        toChainId: sourceChainId,
        fromTokenAddress: swapRoute?.toTokenAddress,
        toTokenAddress: swapRoute?.fromTokenAddress,
      });
  }, [
    destChainId,
    sourceChainId,
    onSwapChange,
    swapRoute?.toTokenAddress,
    swapRoute?.fromTokenAddress,
    supportedSourceChains,
    supportedDestinationChains,
  ]);

  /**
   * Init default chain ids and default token addresses
   * Based on config & available tokens
   */
  useEffect(() => {
    if (tokens.length > 0 && !useSwapRoutePersistStore.getState().swapRoute) {
      const defaultFromChainId = getFirstAvailableChainId(
        {
          initialAssets,
          disabledChains,
          availableChains,
        },
        "from",
        supportedSourceChains
      );

      const defaultToChainId = getInitialChainIdFromConfig({
        chains: supportedDestinationChains,
        config: {
          initialAssets,
          disabledChains,
          availableChains,
        },
        direction: "to",
      });

      const fromTokenAddress = getInitialOrDefaultTokenAddressForChain({
        tokens,
        config: {
          initialAssets,
          defaultTokensPerChain,
          disabledChains,
          availableChains,
        },
        chainId: defaultFromChainId,
        direction: "from",
      });

      const toTokenAddress = getInitialOrDefaultTokenAddressForChain({
        tokens,
        config: {
          initialAssets,
          defaultTokensPerChain,
          disabledChains,
          availableChains,
        },
        chainId: defaultToChainId,
        direction: "to",
        excludeToken: {
          address: fromTokenAddress,
          chainId: defaultFromChainId,
        },
      });

      useSwapRoutePersistStore.setState({
        swapRoute: {
          fromChainId: defaultFromChainId,
          toChainId: defaultToChainId,
          fromTokenAddress,
          toTokenAddress,
          destinationAddress: undefined,
        },
      });
    }
  }, [
    supportedDestinationChains,
    supportedSourceChains,
    initialAssets,
    disabledChains,
    availableChains,
    defaultTokensPerChain,
    tokens,
  ]);

  const isDestAddressConnected = areSameAddress(
    connectedDestinationAddress.address,
    destinationAddress.address
  );

  return {
    tokenItems,
    onSwapChange,
    invertSwaps,
    fromPrice,
    fromPriceChanged,
    toToken,
    fromToken,
    fromChain,
    toChain,
    destinationAddress,
    isDestAddressConnected,
    isSameChain,
    fromTokens,
    toTokens,
  };
};
